/*!
 * Girlbasket Theme v1.0.0
 * Copyright (c) 2026 Om. All rights reserved.
 *
 * This file is part of the Girlbasket theme, licensed only for use on the
 * Girlbasket store (girlbasket.myshopify.com / girlbasket.com). Copying,
 * modifying, redistributing or reselling this file, in whole or in part,
 * without written permission from Om is prohibited. See LICENSE.md.
 *
 * File: assets/gb-product.js
 */
(() => {
  'use strict';

  const GB = window.GB;
  if (!GB || !GB.events) return;

  const money = (cents) => GB.formatMoney(cents);
  const handleize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  /* ---------------------------------------------------------------------
     <gb-variant-picker>
     Finds the variant for the chosen options, updates the form, price and
     URL, refreshes dynamic blocks and fires "gb:variant:change".
     --------------------------------------------------------------------- */
  class GbVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.formId;
      const json = this.querySelector('[data-gb-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.requestId = 0;

      this.addEventListener('change', (event) => {
        if (event.target.dataset.optionPosition) this.onChange(event.target);
      });
      this.updateAvailability(this.selectedOptions());
    }

    selectedOptions() {
      const count = this.variants[0] ? this.variants[0].options.length : 0;
      const options = [];
      for (let position = 1; position <= count; position += 1) {
        const checked = this.querySelector(`input[data-option-position="${position}"]:checked`);
        const select = this.querySelector(`select[data-option-position="${position}"]`);
        options.push(checked ? checked.value : select ? select.value : null);
      }
      return options;
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, i) => value === options[i])) || null;
    }

    onChange(target) {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);
      this.variant = variant;

      const label = target.closest('fieldset')?.querySelector('[data-gb-selected-label]');
      if (label) label.textContent = target.value;

      this.updateAvailability(options);
      this.updateForm(variant);
      this.updatePrice(variant);

      if (variant && this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url.toString());
      }

      GB.events.emit('variant:change', { sectionId: this.sectionId, variant, options, picker: this });
      if (variant && this.hasAttribute('data-render-section')) this.renderSection(variant);
    }

    isValueAvailable(position, value, options) {
      return this.variants.some(
        (variant) =>
          variant.available &&
          variant.options[position - 1] === value &&
          variant.options.every((option, i) => i === position - 1 || options[i] === null || option === options[i])
      );
    }

    updateAvailability(options) {
      if (this.dataset.availability === 'off') return;
      options.forEach((selected, index) => {
        const position = index + 1;
        this.querySelectorAll(`input[data-option-position="${position}"]`).forEach((input) => {
          const available = this.isValueAvailable(position, input.value, options);
          input.closest('.gb-picker__value')?.classList.toggle('is-unavailable', !available);
        });
        this.querySelectorAll(`select[data-option-position="${position}"] option`).forEach((option) => {
          if (!option.dataset.base) option.dataset.base = option.textContent.split(' – ')[0].trim();
          const available = this.isValueAvailable(position, option.value, options);
          option.textContent = available ? option.dataset.base : `${option.dataset.base} – ${GB.strings.soldOut}`;
        });
      });
    }

    updateForm(variant) {
      const form = document.getElementById(this.formId);
      if (!form) return;
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variant ? variant.id : '';
        idInput.disabled = !variant;
      }
      const button = form.querySelector('[data-gb-add-button]');
      const buttonLabel = button && button.querySelector('[data-gb-add-label]');
      if (!button || !buttonLabel) return;
      if (!variant) {
        button.disabled = true;
        buttonLabel.textContent = GB.strings.unavailable;
      } else if (!variant.available) {
        button.disabled = true;
        buttonLabel.textContent = GB.strings.soldOut;
      } else {
        button.disabled = false;
        buttonLabel.textContent = button.dataset.label || GB.strings.addToCart;
      }
    }

    updatePrice(variant) {
      const wrap = document.getElementById(`GbPrice-${this.sectionId}`);
      const price = wrap && wrap.querySelector('[data-gb-price]');
      if (!price || !variant) return;
      const onSale = variant.compare_at_price > variant.price;
      price.classList.toggle('gb-price--sale', onSale);
      price.classList.toggle('gb-price--sold-out', !variant.available);
      const current = price.querySelector('.gb-price__current');
      if (current) current.textContent = money(variant.price);
      let compare = price.querySelector('.gb-price__compare');
      if (onSale) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'gb-price__compare';
          current.after(compare);
        }
        compare.textContent = money(variant.compare_at_price);
      } else if (compare) {
        compare.remove();
      }
    }

    async renderSection(variant) {
      const requestId = (this.requestId += 1);
      const url = `${this.dataset.url}?variant=${variant.id}&section_id=${encodeURIComponent(this.sectionId)}`;
      try {
        const response = await fetch(url);
        if (!response.ok || requestId !== this.requestId) return;
        const doc = GB.parseHtml(await response.text());
        const root = this.closest('[data-gb-product-section]') || document;
        root.querySelectorAll('[data-gb-dynamic][id]').forEach((element) => {
          const fresh = doc.getElementById(element.id);
          if (!fresh) return;
          if (element.tagName === 'DETAILS') fresh.open = element.open;
          element.replaceWith(fresh);
        });
        GB.events.emit('variant:rendered', { sectionId: this.sectionId, variant });
      } catch (error) {
        /* Keep the client-side update if the request fails. */
      }
    }
  }
  customElements.define('gb-variant-picker', GbVariantPicker);

  /* ---------------------------------------------------------------------
     <gb-product-form>: adds to cart without leaving the page
     --------------------------------------------------------------------- */
  class GbProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.error = this.querySelector('[data-gb-form-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      const recipient = this.querySelector('.gb-recipient');
      if (recipient) {
        recipient.addEventListener('toggle', () => {
          recipient
            .querySelectorAll('[data-gb-recipient-field], [data-gb-recipient-flag]')
            .forEach((field) => {
              field.disabled = !recipient.open;
            });
          const email = recipient.querySelector('input[type="email"]');
          if (email) email.required = recipient.open;
        });
      }
    }

    async onSubmit(event) {
      if (GB.settings.cartAfterAdd === 'page' || !GB.cart) return;
      event.preventDefault();
      if (!this.form.reportValidity()) return;

      const button = this.form.querySelector('[data-gb-add-button]');
      if (!button || button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      this.showError('');

      try {
        const item = await GB.cart.add(new FormData(this.form));
        GB.events.emit('cart:add', { item, form: this.form, source: this });
      } catch (error) {
        this.showError(error.message || GB.strings.cartError);
        GB.events.emit('cart:error', { error, form: this.form });
      } finally {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }
  customElements.define('gb-product-form', GbProductForm);

  /* ---------------------------------------------------------------------
     <gb-gallery>: carousel, thumbnails, variant media and zoom
     --------------------------------------------------------------------- */
  class GbGallery extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.track = this.querySelector('[data-gb-track]');
      this.items = [...this.querySelectorAll('.gb-gallery__item')];
      this.thumbs = [...this.querySelectorAll('[data-gb-thumb]')];
      this.dots = [...this.querySelectorAll('.gb-gallery__dot')];
      this.filterPosition = Number(this.dataset.filterPosition) || 0;
      if (!this.track) return;

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(thumb.dataset.gbThumb)));
      this.querySelector('[data-gb-gallery-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gb-gallery-next]')?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', GB.debounce(() => this.syncFromScroll(), 80), { passive: true });

      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-gb-zoom]');
        if (!zoom) return;
        const template = this.querySelector('[data-gb-zoom-images]');
        const viewer = document.getElementById('GbZoom');
        if (template && viewer) viewer.show(template.innerHTML, Number(zoom.dataset.gbZoom), zoom);
      });

      this.unsubscribe = GB.events.on('variant:change', ({ sectionId, variant }) => {
        if (sectionId !== this.sectionId || !variant) return;
        if (this.filterPosition) this.filter(handleize(variant.options[this.filterPosition - 1]));
        if (variant.featured_media) this.show(String(variant.featured_media.id));
      });

      if (this.filterPosition) {
        const active = this.items.find((item) => item.classList.contains('is-active'));
        if (active && active.dataset.group) this.filter(active.dataset.group);
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    get visibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    isCarousel() {
      return this.track.scrollWidth > this.track.clientWidth + 2;
    }

    show(mediaId) {
      const item = this.items.find((entry) => entry.dataset.mediaId === String(mediaId));
      if (!item || item.hidden) return;
      this.setActive(item);
      if (this.isCarousel()) {
        this.track.scrollTo({ left: GB.offsetIn(item, this.track), behavior: 'smooth' });
      } else {
        this.items.forEach((entry) => entry.style.removeProperty('order'));
        item.style.order = '-1';
      }
    }

    step(direction) {
      const visible = this.visibleItems;
      const index = visible.findIndex((item) => item.classList.contains('is-active'));
      const next = visible[(index + direction + visible.length) % visible.length];
      if (next) this.show(next.dataset.mediaId);
    }

    syncFromScroll() {
      if (!this.isCarousel()) return;
      const left = this.track.scrollLeft;
      let best = null;
      let distance = Infinity;
      this.visibleItems.forEach((item) => {
        const gap = Math.abs(GB.offsetIn(item, this.track) - left);
        if (gap < distance) {
          distance = gap;
          best = item;
        }
      });
      if (best) this.setActive(best);
    }

    setActive(item) {
      this.items.forEach((entry) => {
        const active = entry === item;
        entry.classList.toggle('is-active', active);
        if (!active) entry.querySelectorAll('video').forEach((video) => video.pause());
      });
      const id = item.dataset.mediaId;
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.gbThumb === id;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
      this.dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.mediaId === id));
    }

    filter(group) {
      if (!group) return;
      const matches = this.items.some((item) => item.dataset.group === group);
      if (!matches) return;
      this.items.forEach((item) => {
        item.hidden = Boolean(item.dataset.group) && item.dataset.group !== group;
        const thumb = this.querySelector(`.gb-gallery__thumb-item[data-media-id="${item.dataset.mediaId}"]`);
        if (thumb) thumb.hidden = item.hidden;
        const dot = this.dots.find((entry) => entry.dataset.mediaId === item.dataset.mediaId);
        if (dot) dot.hidden = item.hidden;
      });
    }
  }
  customElements.define('gb-gallery', GbGallery);

  /* ---------------------------------------------------------------------
     <gb-zoom>: full-screen image viewer with tap, drag and pinch zoom
     --------------------------------------------------------------------- */
  class GbZoom extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('gb-drawer') || this.querySelector('gb-drawer');
      this.track = this.querySelector('[data-gb-zoom-track]');
      this.querySelector('[data-gb-zoom-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gb-zoom-next]')?.addEventListener('click', () => this.step(1));
      this.pointers = new Map();

      this.track.addEventListener('click', (event) => {
        const slide = event.target.closest('.gb-zoom__slide');
        if (!slide || this.dragged) return;
        this.toggleZoom(slide, event);
      });
      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event));
      ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) =>
        this.track.addEventListener(type, (event) => this.onPointerUp(event))
      );
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.step(1);
        if (event.key === 'ArrowLeft') this.step(-1);
      });
    }

    show(html, index, opener) {
      this.track.innerHTML = html;
      this.drawer.open(opener);
      requestAnimationFrame(() => {
        const slide = this.track.querySelector(`.gb-zoom__slide[data-index="${index}"]`);
        if (slide) this.track.scrollTo({ left: GB.offsetIn(slide, this.track), behavior: 'instant' });
      });
    }

    step(direction) {
      this.track.querySelectorAll('.is-zoomed').forEach((slide) => slide.classList.remove('is-zoomed'));
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
    }

    toggleZoom(slide, event) {
      const zoomed = slide.classList.toggle('is-zoomed');
      this.track.classList.toggle('is-locked', zoomed);
      if (!zoomed) return;
      const rect = slide.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      requestAnimationFrame(() => {
        slide.scrollLeft = x * slide.scrollWidth - slide.clientWidth / 2;
        slide.scrollTop = y * slide.scrollHeight - slide.clientHeight / 2;
      });
    }

    onPointerDown(event) {
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.dragged = false;
      const slide = event.target.closest('.gb-zoom__slide');
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinchStart = Math.hypot(a.x - b.x, a.y - b.y);
        this.pinchSlide = slide;
      } else if (slide && slide.classList.contains('is-zoomed') && event.pointerType === 'mouse') {
        this.drag = { slide, x: event.clientX, y: event.clientY, left: slide.scrollLeft, top: slide.scrollTop };
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2 && this.pinchSlide) {
        const [a, b] = [...this.pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const zoomed = this.pinchSlide.classList.contains('is-zoomed');
        if (!zoomed && distance - this.pinchStart > 40) this.toggleZoom(this.pinchSlide, event);
        if (zoomed && this.pinchStart - distance > 40) this.toggleZoom(this.pinchSlide, event);
        return;
      }
      if (this.drag) {
        const dx = event.clientX - this.drag.x;
        const dy = event.clientY - this.drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) this.dragged = true;
        this.drag.slide.scrollLeft = this.drag.left - dx;
        this.drag.slide.scrollTop = this.drag.top - dy;
      }
    }

    onPointerUp(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinchSlide = null;
      this.drag = null;
      setTimeout(() => {
        this.dragged = false;
      }, 0);
    }
  }
  customElements.define('gb-zoom', GbZoom);

  /* ---------------------------------------------------------------------
     <gb-sticky-atc>: bar shown once the main buy button is out of view
     --------------------------------------------------------------------- */
  class GbStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.button = this.querySelector('[data-gb-sticky-button]');
      if (!this.form) return;

      const target = this.form.querySelector('[data-gb-add-button]') || this.form;
      this.observer = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggle(passed);
      });
      this.observer.observe(target);

      this.button.addEventListener('click', () => {
        const scrollOnly =
          this.dataset.behavior === 'scroll' || this.form.querySelector('[data-gb-add-button]')?.disabled;
        if (scrollOnly) {
          this.form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.form.requestSubmit(this.form.querySelector('[data-gb-add-button]'));
      });

      this.unsubscribe = GB.events.on('variant:change', ({ sectionId, variant }) => {
        if (sectionId !== this.dataset.sectionId) return;
        const title = this.querySelector('[data-gb-sticky-variant]');
        const price = this.querySelector('[data-gb-sticky-price]');
        if (title) title.textContent = variant ? variant.title : GB.strings.unavailable;
        if (price && variant) price.textContent = money(variant.price);
        if (this.dataset.behavior !== 'scroll') {
          this.button.disabled = !variant || !variant.available;
          this.button.textContent = !variant
            ? GB.strings.unavailable
            : variant.available
              ? GB.strings.addToCart
              : GB.strings.soldOut;
        }
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      document.body.classList.remove('gb-has-sticky-atc');
    }

    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.button.tabIndex = show ? 0 : -1;
      document.body.classList.toggle('gb-has-sticky-atc', show);
    }
  }
  customElements.define('gb-sticky-atc', GbStickyAtc);

  /* ---------------------------------------------------------------------
     <gb-recommendations>: product recommendations loaded when near view
     --------------------------------------------------------------------- */
  class GbRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        const doc = GB.parseHtml(await response.text());
        const fresh = doc.getElementById(this.dataset.blockId);
        const target = this.querySelector(`#${CSS.escape(this.dataset.blockId)}`);
        if (fresh && target && fresh.innerHTML.trim()) {
          target.innerHTML = fresh.innerHTML;
          this.classList.add('is-loaded');
          GB.observeReveal(this);
        } else if (!this.classList.contains('gb-product__block')) {
          this.hidden = true;
        }
      } catch (error) {
        this.hidden = true;
      }
    }
  }
  customElements.define('gb-recommendations', GbRecommendations);

  /* ---------------------------------------------------------------------
     <gb-pickup>: store pickup availability for the selected variant
     --------------------------------------------------------------------- */
  class GbPickup extends HTMLElement {
    connectedCallback() {
      this.load(this.dataset.variantId);
      const section = this.closest('gb-product-form')?.dataset.sectionId;
      this.unsubscribe = GB.events.on('variant:change', ({ sectionId, variant }) => {
        if (sectionId !== section) return;
        if (variant && variant.available) this.load(variant.id);
        else this.innerHTML = '';
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    async load(variantId) {
      if (!variantId) return;
      const base = (this.dataset.baseUrl || '/').replace(/\/$/, '');
      try {
        const response = await fetch(`${base}/variants/${variantId}/?section_id=gb-pickup-availability`);
        if (!response.ok) throw new Error(response.status);
        const doc = GB.parseHtml(await response.text());
        const section = doc.querySelector('.shopify-section') || doc.body;
        this.innerHTML = section.innerHTML.trim();
      } catch (error) {
        this.innerHTML = '';
      }
    }
  }
  customElements.define('gb-pickup', GbPickup);

  /* ---------------------------------------------------------------------
     Quick buy: opens the product form in a modal
     --------------------------------------------------------------------- */
  const quickCache = new Map();

  GB.quickBuy = {
    async open(url, opener) {
      const modal = document.getElementById('GbQuickBuy');
      if (!modal) {
        window.location.href = url;
        return;
      }
      const body = modal.querySelector('[data-gb-quick-body]');
      body.innerHTML = '<div class="gb-loader" role="status"><span class="gb-spinner"></span></div>';
      modal.open(opener);

      try {
        let html = quickCache.get(url);
        if (!html) {
          const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=gb-quick-buy`);
          if (!response.ok) throw new Error(response.status);
          const doc = GB.parseHtml(await response.text());
          const content = doc.querySelector('[data-gb-quick-content]');
          if (!content) throw new Error('No content');
          html = content.outerHTML;
          quickCache.set(url, html);
        }
        body.innerHTML = html;
        // Script tags inserted with innerHTML do not run; the JSON one is only read.
        const first = body.querySelector('input, button, a, select');
        if (first) first.focus({ preventScroll: true });
      } catch (error) {
        window.location.href = url;
      }
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-gb-quick-trigger]');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const holder = trigger.closest('[data-gb-quick-url]');
    const url = trigger.dataset.gbQuickUrl || (holder && holder.dataset.gbQuickUrl);
    if (!url) return;
    event.preventDefault();
    GB.quickBuy.open(url, trigger);
  });

  GB.events.on('cart:add', ({ form }) => {
    const modal = document.getElementById('GbQuickBuy');
    if (modal && modal.isOpen && form && modal.contains(form)) modal.close();
  });

  /* ---------------------------------------------------------------------
     Product cards: add to cart button (single-variant products)
     --------------------------------------------------------------------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-gb-card-atc]');
    if (!form || GB.settings.cartAfterAdd === 'page' || !GB.cart) return;
    event.preventDefault();
    const button = form.querySelector('button');
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    try {
      const item = await GB.cart.add(new FormData(form));
      GB.events.emit('cart:add', { item, form, source: form });
    } catch (error) {
      GB.toast(error.message || GB.strings.cartError, { type: 'error' });
      GB.events.emit('cart:error', { error, form });
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  });

  /* ---------------------------------------------------------------------
     Product cards: hovering a colour swatch shows that colour's image
     --------------------------------------------------------------------- */
  document.addEventListener('pointerover', (event) => {
    const swatch = event.target.closest('.gb-card [data-gb-swatch-image]');
    if (!swatch) return;
    const image = swatch.closest('.gb-card').querySelector('.gb-card__img--main');
    if (!image || image.dataset.gbSwatch === swatch.dataset.gbSwatchImage) return;
    image.dataset.gbSwatch = swatch.dataset.gbSwatchImage;
    image.srcset = swatch.dataset.gbSwatchSrcset || '';
    image.src = swatch.dataset.gbSwatchImage;
  });
})();
